import { Form, FormikProvider, useFormik } from "formik";
import { z } from "zod";
import { OrganizationCloseStageCreateSchema } from "@common/contracts/organization-close-stage.contract";
import { OrganizationCloseStage } from "@common/types/organizationCloseStage";
import {
    useCreateOrganizationCloseStageMutation,
    useUpdateOrganizationCloseStageMutation,
} from "@api/organizationCloseStage.api";
import { submitHelper, validateFormikWithZodSchema } from "@helpers/form";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { createFormikFields } from "@components/forms/fields/createFormikFields";
import { PlainModal, StandardModalHeader } from "@design-system/Modal/Modal";
import { HStack } from "@design-system/HStack/HStack";
import { VStack } from "@design-system/VStack/VStack";

type CloseStageFormValues = z.infer<typeof OrganizationCloseStageCreateSchema>;

const fields = createFormikFields<CloseStageFormValues>();

interface Props {
    show: boolean;
    onHide: () => void;
    closeStage?: OrganizationCloseStage;
}

export const OrganizationCloseStageFormModal = ({
    show,
    onHide,
    closeStage,
}: Props) => {
    const { error: errorToast } = useToaster();
    const createCloseStage = useCreateOrganizationCloseStageMutation();
    const updateCloseStage =
        useUpdateOrganizationCloseStageMutation(closeStage);

    const isEditMode = !!closeStage;
    const isSubmitting =
        createCloseStage.isPending || updateCloseStage.isPending;

    const formik = useFormik<CloseStageFormValues>({
        initialValues: {
            name: closeStage?.name ?? "",
            parentCloseStageId: null,
        },
        enableReinitialize: true,
        validate: validateFormikWithZodSchema(
            OrganizationCloseStageCreateSchema,
        ),
        validateOnMount: true,
        onSubmit: submitHelper({
            loading: isSubmitting,
            handler: async (values) => {
                const payload = {
                    name: values.name.trim(),
                    parentCloseStageId: isEditMode ? undefined : null,
                };
                if (isEditMode) {
                    await updateCloseStage.mutateAsync({ name: payload.name });
                } else {
                    await createCloseStage.mutateAsync(payload);
                }
                formik.resetForm();
                onHide();
            },
            failureHandler: async () => {
                errorToast(
                    isEditMode
                        ? "Failed to update stage"
                        : "Failed to create stage",
                );
            },
        }),
    });

    const title = isEditMode ? "Edit stage" : "Create stage";

    const handleHide = () => {
        formik.resetForm();
        onHide();
    };

    return (
        <PlainModal show={show} onHide={handleHide}>
            <StandardModalHeader>{title}</StandardModalHeader>
            <FormikProvider value={formik}>
                <Form onSubmit={formik.handleSubmit}>
                    <VStack gap={16}>
                        <fields.Text name="name" label="Name" size="xl" />
                        <HStack as="footer" justify="end" gap={12}>
                            <Button
                                variant="secondary"
                                size="xl"
                                type="button"
                                onClick={handleHide}
                                disabled={isSubmitting}
                            >
                                Cancel
                            </Button>
                            <ButtonWithLoader
                                variant="primary"
                                size="xl"
                                type="submit"
                                loading={isSubmitting}
                                disabled={!formik.isValid}
                            >
                                {isEditMode ? "Save" : "Create"}
                            </ButtonWithLoader>
                        </HStack>
                    </VStack>
                </Form>
            </FormikProvider>
        </PlainModal>
    );
};
